import React, { useEffect, useState } from 'react';
import { fetchGet, fetchPost } from '../../api/common';
import { DailyStats, Maintenance } from '../room/types';
import {
  Button,
  Grid,
  Switch,
  Stack,
  TextField,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import { Socket } from 'socket.io-client';
import { StyledPaperItem } from '../../common/mui-components';
import { DailyStatsTable } from './DailyStatsTable';
import { AdminAllPlayersDialog } from '../../dialog/AdminAllPlayersDialog';

type PropTypes = {
  enabledTeams: boolean;
  maintenance: Maintenance;
  profileId?: string;
  socket: Socket;
  setTestingFeature: (testing: boolean) => void;
  testingFeature: boolean;
};

export const Admin = ({
  enabledTeams,
  maintenance,
  profileId,
  socket,
  setTestingFeature,
  testingFeature,
}: PropTypes) => {
  const [adminMaintenance, setAdminMaintenance] = useState<Maintenance>();
  const [maintenancePassword, setMaintenancePassword] = useState('');
  const [maintenanceError, setMaintenanceError] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [resetPlayerProfileId, setResetPlayerProfileId] = useState('');
  const [forceDeleteGameId, setForceDeleteGameId] = useState('');
  const [gameDataId, setGameDataId] = useState('');
  const [bannerMessage, setBannerMessage] = useState('');
  const [userStatusProfileId, setUserStatusProfileId] = useState('');
  const [dailyStats, setDailyStats] = useState<DailyStats[]>();
  const [fuzzyAnswer, setFuzzyAnswer] = useState('');
  const [fuzzyPlayerInput, setFuzzyPlayerInput] = useState('');
  const [fuzzyResults, setFuzzyResults] = useState<any>();
  const [openAllPlayersDialog, setOpenAllPlayersDialog] = useState(false);
  const [betaTourneyEmail, setBetaTourneyEmail] = useState('');

  useEffect(() => {
    fetchDailyStats();
    socket.on('allConnectedSockets', console.log);
    return () => {
      socket.off('allConnectedSockets');
    };
  }, []);

  useEffect(() => {
    setAdminMaintenance(maintenance);
  }, [maintenance]);

  const handleGetAllSockets = () => {
    socket.emit('getAllConnectedSockets');
  };

  const handleScheduleMaintenanceSwitch = (checked: boolean) => {
    setMaintenanceError('');
    setAdminMaintenance((prev: any) => ({ ...prev, scheduled: checked }));
    socket.emit(
      'maintenance',
      {
        scheduled: checked,
        bannerMessage: adminMaintenance?.bannerMessage,
        password: maintenancePassword,
      },
      maintenanceCallback
    );
  };

  const maintenanceCallback = (error: string) => {
    if (error === 'Wrong Password') {
      setAdminMaintenance((prev: any) => ({ ...prev, scheduled: false }));
      setMaintenanceError(error);
    }
  };

  const handleBannerMessage = (message: string) => {
    setAdminMaintenance((prev: any) => ({ ...prev, bannerMessage: message }));
  };

  const handlePassword = (password: string) => {
    setMaintenancePassword(password);
  };

  const handleGenerateInviteCode = async () => {
    const res = await fetchPost('/admin/generate-invite-code', {});
    setInviteCode(res.inviteCode);
  };

  const handleResetPlayerProfileId = (input: string) => {
    setResetPlayerProfileId(input);
  };

  const handleResetPlayer = (profileId: string) => {
    if (!profileId) return;
    socket.emit('resetPlayerStatus', {
      profileId: profileId,
    });
  };

  const handleBrowserRefresh = () => {
    socket.emit('latestClientVersion', profileId);
  };

  const handlePrecision = async () => {
    // const res = await fetchGet('/admin/precision-report');
    socket.emit('fix');
  };

  const fetchDailyStats = async () => {
    const res: DailyStats[] = await fetchGet('/admin/daily-stats');
    const reverse = res.sort((a, b) => b.date - a.date);
    setDailyStats(reverse);
  };

  const handleForceDeleteGameOnChange = (input: string) =>
    setForceDeleteGameId(input);

  const handleGameDataIdOnChange = (input: string) => setGameDataId(input);

  const handleGetUserProfiledIdOnChange = (input: string) =>
    setUserStatusProfileId(input);

  const handleForceDeleteGame = () => {
    if (!forceDeleteGameId || !profileId) return;
    socket.emit('forceDeleteGame', {
      gameId: forceDeleteGameId,
      profileId: profileId,
    });
  };

  const handleGetGameStatus = async () => {
    const res = await fetchGet(`/admin/game-on-server/${gameDataId}`);
    console.log('handleGetGameStatus', res);
  };

  const handleGetUserStatus = async () => {
    const res = await fetchGet(`/admin/logged-in-user/${userStatusProfileId}`);
    console.log('handleGetUserStatus', res);
  };

  const handleBetaTourneyEmail = async () => {
    const res = await fetchPost(
      `/authenticate/beta-tourney-email/${betaTourneyEmail}`
    );
  };

  const handleConnectedSockets = async () => {
    socket.emit('checkConnectedUsers');
  };

  const handlePingAllClients = () => {
    socket.emit('pingAllClients', { profileId });
  };

  const handleFuzzyAnswer = (input: string) => {
    setFuzzyAnswer(input);
  };

  const handleFuzzyPlayerInput = (input: string) => {
    setFuzzyPlayerInput(input);
  };

  const handleSubmitFuzzy = async () => {
    const res = await fetchPost('/admin/fuzzy-tester', {
      answer: fuzzyAnswer,
      playerInput: fuzzyPlayerInput,
    });
    setFuzzyResults(res);
    console.log('handleSubmitFuzzy', res);
  };

  const handleBannerMessageClick = (clear: boolean) => {
    socket.emit('bannerMessage', {
      profileId,
      bannerMessage: bannerMessage,
      clear,
    });
  };

  const handleEnableTeams = (checked: boolean) => {
    socket.emit('enableTeams', { enabled: checked, profileId });
  };

  return (
    <Box sx={{ paddingBottom: 20 }}>
      <AdminAllPlayersDialog
        close={() => setOpenAllPlayersDialog(false)}
        handleResetPlayer={handleResetPlayer}
        open={openAllPlayersDialog}
      />
      <Grid container spacing={2}>
        {/* <Grid item xs={4}>
          <StyledPaperItem>
            <Typography variant="h5" mb={2}>
              Generate Invite Code
            </Typography>
            <Button onClick={handleGenerateInviteCode}>Generate</Button>
            <Typography variant="h5" mb={2}>
              Invite code: {inviteCode}
            </Typography>
          </StyledPaperItem>
          <StyledPaperItem sx={{ marginTop: 2 }}>
            <Tooltip
              title="Currently in use to test the new game buttons on Main"
              placement="top"
            >
              <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
                px={2}
              >
                <Typography variant="body1">Test hidden feature</Typography>
                <Switch
                  checked={testingFeature}
                  onChange={(e) => setTestingFeature(e.target.checked)}
                />
              </Stack>
            </Tooltip>
          </StyledPaperItem>
        </Grid> */}
        <Grid item xs={4}>
          <StyledPaperItem>
            <Stack
              direction={'row'}
              mt={2}
              justifyContent={'center'}
              alignItems={'center'}
            >
              <Typography variant="body1">Enable Teams</Typography>
              <Switch
                checked={enabledTeams}
                onChange={(e) => handleEnableTeams(e.target.checked)}
              />
            </Stack>
          </StyledPaperItem>
        </Grid>

        <Grid item xs={4}>
          <StyledPaperItem>
            <Stack>
              {maintenanceError ? (
                <Typography color="error.main">{maintenanceError}</Typography>
              ) : null}
              <Typography variant="h5">Schedule Maintenance</Typography>
              <TextField
                id="bannerMessage"
                label="Banner message"
                variant="standard"
                onChange={(e) => handleBannerMessage(e.target.value)}
              />
              <TextField
                id="password"
                label="Password"
                type={'password'}
                variant="standard"
                onChange={(e) => handlePassword(e.target.value)}
              />
              <Stack direction={'row'} mt={2}>
                <Typography variant="body1">Set to maintenance mode</Typography>
                <Switch
                  checked={adminMaintenance?.scheduled || false}
                  onChange={(e) =>
                    handleScheduleMaintenanceSwitch(e.target.checked)
                  }
                />
              </Stack>
            </Stack>
          </StyledPaperItem>
        </Grid>
        <Grid item xs={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <StyledPaperItem>
                {/* <Button onClick={handleGetAllSockets}>Get all sockets</Button> */}
                <TextField
                  id="enterProfileId"
                  label="Enter Profile ID"
                  variant="standard"
                  onChange={(e) => handleResetPlayerProfileId(e.target.value)}
                />
                <Button onClick={() => handleResetPlayer(resetPlayerProfileId)}>
                  Reset Player Status
                </Button>
              </StyledPaperItem>
            </Grid>
            <Grid item xs={12}>
              <StyledPaperItem>
                <Button onClick={handleBrowserRefresh}>
                  Force Browser Refreshes
                </Button>
              </StyledPaperItem>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4}>
          <StyledPaperItem>
            <Button onClick={handlePrecision}>Get Precision</Button>
          </StyledPaperItem>
        </Grid>
        <Grid item xs={4}>
          <StyledPaperItem>
            <TextField
              id="enterGameId"
              label="Game ID"
              variant="standard"
              onChange={(e) => handleForceDeleteGameOnChange(e.target.value)}
            />
            <Button onClick={handleForceDeleteGame}>Force Delete Game</Button>
          </StyledPaperItem>
        </Grid>
        <Grid item xs={4}>
          <StyledPaperItem>
            <Button onClick={handleConnectedSockets}>Connected Sockets</Button>
          </StyledPaperItem>
        </Grid>
        <Grid item xs={4}>
          <StyledPaperItem>
            <Button onClick={() => setOpenAllPlayersDialog(true)}>
              View All Players
            </Button>
          </StyledPaperItem>
        </Grid>
        <Grid item xs={4}>
          <StyledPaperItem>
            <TextField
              id="enterGameId"
              label="Game ID"
              variant="standard"
              onChange={(e) => handleGameDataIdOnChange(e.target.value)}
            />
            <Button onClick={handleGetGameStatus}>Get Game Status</Button>
          </StyledPaperItem>
        </Grid>
        <Grid item xs={4}>
          <StyledPaperItem>
            <TextField
              id="enterProfileId"
              label="Profile ID"
              variant="standard"
              onChange={(e) => handleGetUserProfiledIdOnChange(e.target.value)}
            />
            <Button onClick={handleGetUserStatus}>Get User Status</Button>
          </StyledPaperItem>
        </Grid>
        <Grid item xs={4}>
          <StyledPaperItem>
            <TextField
              id="emailBetaTourney"
              label="email"
              variant="standard"
              onChange={(e) => setBetaTourneyEmail(e.target.value)}
            />
            <Button onClick={handleBetaTourneyEmail}>Send Beta Email</Button>
          </StyledPaperItem>
        </Grid>
        <Grid item xs={12} sm={12}>
          <StyledPaperItem>
            <Stack direction={'row'}>
              <TextField
                id="bannerMessage"
                label="Banner Message"
                variant="standard"
                fullWidth
                onChange={(e) => setBannerMessage(e.target.value)}
              />
              <Button onClick={() => handleBannerMessageClick(false)}>
                Update Banner Message
              </Button>
              <Button onClick={() => handleBannerMessageClick(true)}>
                Clear Banner Message
              </Button>
            </Stack>
          </StyledPaperItem>
        </Grid>
        <Grid item xs={12} sm={6}>
          <StyledPaperItem>
            <DailyStatsTable dailyStats={dailyStats}></DailyStatsTable>
          </StyledPaperItem>
        </Grid>
        <Grid item xs={12} sm={6}>
          <StyledPaperItem>
            <Typography>Fuzzy Tester</Typography>
            <Stack
              spacing={2}
              sx={{
                display: 'flex',
                justifyContent: 'left',
                textAlign: 'left',
              }}
            >
              <TextField
                id="answer"
                label="Answer (in database)"
                variant="standard"
                onChange={(e) => handleFuzzyAnswer(e.target.value)}
              />
              <TextField
                id="playerInput"
                label="Player's Input"
                variant="standard"
                onChange={(e) => handleFuzzyPlayerInput(e.target.value)}
              />
              <Button
                onClick={handleSubmitFuzzy}
                sx={{
                  display: 'flex',
                  justifyContent: 'left',
                }}
              >
                Get Fuzzy Result
              </Button>
              <Typography>
                Accepted: {fuzzyResults?.result ? 'Yes' : 'No'}
              </Typography>
              <Typography>Fuzzy Score: {fuzzyResults?.fuzzyScore}</Typography>
              <Typography>
                matches: {JSON.stringify(fuzzyResults?.matches)}
              </Typography>
              <Typography>
                scores: {JSON.stringify(fuzzyResults?.fuzzyScores)}
              </Typography>
            </Stack>
          </StyledPaperItem>
        </Grid>
      </Grid>
    </Box>
  );
};
